import { buildUrl, API_CONFIG } from './config';
import { Step, Question, Template, LoginResponse, UserResponse, Organization } from '../types';

// Generic API response type
interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// Generic fetch wrapper with authentication
const fetchApi = async <T>(url: string, options?: RequestInit): Promise<T> => {
  try {
    const token = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add existing headers if any
    if (options?.headers) {
      const existingHeaders = options.headers as Record<string, string>;
      Object.assign(headers, existingHeaders);
    }

    // Add authorization header if token exists (except for login endpoint)
    if (token && !url.includes('/auth/login')) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      // Handle token expiration (but don't redirect on login failures)
      if (response.status === 401 && token && !url.includes('/auth/login')) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Session expired. Redirecting to login...');
      }

      // Try to parse error response to get the actual message
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        // If we can't parse the error response, keep the generic message
        console.warn('Could not parse error response:', parseError);
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// API functions
export const apiService = {
  // Authentication endpoints
  login: async (email: string, password: string): Promise<LoginResponse> => {
    return await fetchApi<LoginResponse>(buildUrl('/api/auth/login'), {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  // Get all steps
  getSteps: async (): Promise<Step[]> => {
    return await fetchApi<Step[]>(buildUrl(API_CONFIG.ENDPOINTS.STEPS));
  },

  // Get all templates
  getAllTemplates: async (): Promise<Template[]> => {
    return await fetchApi<Template[]>(buildUrl('/api/templates'));
  },

  // Get management templates
  getTemplatesViaType: async (type: 'financial' | 'management'): Promise<Template[]> => {
    return await fetchApi<Template[]>(buildUrl(`/api/templates?template_type=${type}`));
  },

  // Get financial exercise session (test endpoint)
  getFinancialExerciseSession: async (sessionId: number): Promise<any> => {
    return await fetchApi<any>(buildUrl(`/api/financial/sessions/${sessionId}`));
  },

  // Reset financial exercise session (test endpoint)
  resetFinancialExerciseSession: async (sessionId: number): Promise<any> => {
    return await fetchApi<any>(buildUrl(`/api/financial/sessions/${sessionId}/reset`), {
      method: 'POST',
    });
  },

  // Get active financial session for the logged in user
  getActiveFinancialSession: async (): Promise<any> => {
    return await fetchApi<any>(buildUrl('/api/financial/sessions/active'));
  },

  // Get current financial template assigned to user
  getCurrentFinancialTemplate: async (): Promise<any> => {
    return await fetchApi<any>(buildUrl('/api/templates/financial/current'));
  },

  // Get financial session data
  getFinancialSession: async (): Promise<any> => {
    return await fetchApi<any>(buildUrl('/api/financial/session'));
  },

  // Get financial categories by type
  getFinancialCategories: async (type: string): Promise<any> => {
    return await fetchApi<any>(buildUrl(`/api/financial/categories?type=${type}`));
  },

  // Submit financial practice answers
  submitFinancialAnswers: async (answers: any, scenarioId: number): Promise<any> => {
    return await fetchApi<any>(buildUrl('/api/financial/submit'), {
      method: 'POST',
      body: JSON.stringify({ answers, scenario_id:scenarioId }),
    });
  },

  // Navigate to specific scenario
  navigateToScenario: async (scenarioNumber: number): Promise<any> => {
    return await fetchApi<any>(buildUrl('/api/financial/navigate'), {
      method: 'POST',
      body: JSON.stringify({ scenario_number: scenarioNumber }),
    });
  },

  // Get all organizations
  getAllOrganizations: async (): Promise<Organization[]> => {
    return await fetchApi<Organization[]>(buildUrl('/api/organizations'));
  },

  // Create a new organization
  createOrganization: async (name: string): Promise<Organization> => {
    return await fetchApi<Organization>(buildUrl('/api/organizations'), {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  // Update an organization
  updateOrganization: async (organizationId: number, data: { name: string }): Promise<Organization> => {
    return await fetchApi<Organization>(buildUrl(`/api/organizations/${organizationId}`), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Create a new user
  createUser: async (userData: {
    name: string;
    email: string;
    jobTitle?: string;
    organizationId: number;
    password: string;
    sendWelcomeEmail: boolean;
  }): Promise<any> => {
    return await fetchApi<any>(buildUrl('/api/users'), {
      method: 'POST',
      body: JSON.stringify({
        name: userData.name,
        email: userData.email,
        job_title: userData.jobTitle,
        organization_id: userData.organizationId,
        password: userData.password,
        send_welcome_email: userData.sendWelcomeEmail,
      }),
    });
  },

  // Update an existing user
  updateUser: async (userId: number, userData: {
    name: string;
    email: string;
    jobTitle?: string;
    password?: string;
  }): Promise<any> => {
    const body: any = {
      name: userData.name,
      email: userData.email,
      job_title: userData.jobTitle,
    };

    if (userData.password) {
      body.password = userData.password;
    }

    return await fetchApi<any>(buildUrl(`/api/users/${userId}`), {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  // Update user training status
  updateUserTrainingStatus: async (userId: number, hasViewedTraining: boolean): Promise<any> => {
    return await fetchApi<any>(buildUrl(`/api/users/${userId}/training-status`), {
      method: 'PATCH',
      body: JSON.stringify({
        hasViewedManagementTraining: hasViewedTraining
      }),
    });
  },

  // Delete a user
  deleteUser: async (userId: number): Promise<void> => {
    await fetchApi<void>(buildUrl(`/api/users/${userId}`), {
      method: 'DELETE',
    });
  },

  // Get templates for a specific organization
  getOrganizationTemplates: async (organizationId: number): Promise<Template[]> => {
    return await fetchApi<Template[]>(buildUrl(`/api/organizations/${organizationId}/templates`));
  },

  // Get a specific template with questions
  getTemplate: async (templateId: number): Promise<Template> => {
    return await fetchApi<Template>(buildUrl(`/api/templates/${templateId}`));
  },

  // Update a template
  updateTemplate: async (templateId: number, templateData: Partial<Pick<Template, 'name' | 'description' | 'category'>>): Promise<Template> => {
    return await fetchApi<Template>(buildUrl(`/api/templates/${templateId}`), {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  },

  // Create a new question
  createQuestion: async (question: Omit<Question, 'id' | 'created_at' | 'updated_at'>): Promise<Question> => {
    return await fetchApi<Question>(buildUrl('/api/questions'), {
      method: 'POST',
      body: JSON.stringify(question),
    });
  },

  // Update an existing question
  updateQuestion: async (id: number, question: Partial<Omit<Question, 'id' | 'created_at' | 'updated_at'>>): Promise<Question> => {
    return await fetchApi<Question>(buildUrl(`/api/questions/${id}`), {
      method: 'PUT',
      body: JSON.stringify(question),
    });
  },

  // Delete a question
  deleteQuestion: async (id: number): Promise<void> => {
    await fetchApi<void>(buildUrl(`/api/questions/${id}`), {
      method: 'DELETE',
    });
  },

  // Reorder a question (change step and/or order)
  reorderQuestion: async (id: number, newStepNumber: number, newOrder: number): Promise<Question> => {
    return await fetchApi<Question>(buildUrl(`/api/questions/${id}/reorder`), {
      method: 'PUT',
      body: JSON.stringify({
        step_number: newStepNumber,
        question_order: newOrder,
      }),
    });
  },

  // Duplicate a template
  duplicateTemplate: async (templateId: number, name: string, description: string, organizationId?: number): Promise<Template> => {
    const body: any = {
      name,
      description
    };
    
    if (organizationId) {
      body.organization_id = organizationId;
    }

    return await fetchApi<Template>(buildUrl(`/api/templates/${templateId}/duplicate`), {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Delete a template
  deleteTemplate: async (templateId: number): Promise<void> => {
    await fetchApi<void>(buildUrl(`/api/templates/${templateId}`), {
      method: 'DELETE',
    });
  },

  // Get user responses for all templates
  getUserResponses: async (userId: number): Promise<UserResponse[]> => {
    return await fetchApi<UserResponse[]>(buildUrl(`/api/responses/user/${userId}`));
  },

  // Get all user responses for an organization
  getOrganizationResponses: async (organizationId: number): Promise<UserResponse[]> => {
    return await fetchApi<UserResponse[]>(buildUrl(`/api/organizations/${organizationId}/users/responses`));
  },

  // Create new response
  createResponse: async (data: {
    user_id: number;
    template_id: number;
    question_id: number;
    response_text: string;
    interval: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  }): Promise<UserResponse> => {
    return await fetchApi<UserResponse>(buildUrl('/api/responses'), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Update existing response
  updateResponse: async (responseId: number, data: {
    response_text: string;
    interval: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  }): Promise<UserResponse> => {
    return await fetchApi<UserResponse>(buildUrl(`/api/responses/${responseId}`), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Delete response
  deleteResponse: async (responseId: number): Promise<void> => {
    await fetchApi<void>(buildUrl(`/api/responses/${responseId}`), {
      method: 'DELETE',
    });
  },

  // Download PDF for template
  downloadTemplatePdf: async (templateId: number, userId: number): Promise<Blob> => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(buildUrl(`/api/templates/${templateId}/pdf/${userId}`), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    return await response.blob();
  },
};
